import React, { Suspense, useState } from 'react';
import { MenuList } from '@wikifactory/voxel-ui';
import { observer } from 'mobx-react';

import { useClientStore } from 'apps/hooks';
import { NewTeamIcon, ToggleIcon } from 'icons';
import { capitalizeFirstLetter } from 'utils/formatStrings';
import { Menu, MenuCardSkeleton } from 'components';
import { StartFreeTrail } from 'constants/subscription';
import { NewTeamWorkSpace } from 'apps/components/NewTeamWorkSpace';
import { CreateTeamCard } from 'apps/onboarding/subscription/createTeamCard';
import { themes } from 'styles/themes/wikifactoryNew';
import { getInitiativeMenus } from 'config/menu';
import WorkspaceSwitcherMenuItems from '../WorkspaceSwitcherMenuItems';
import { MenuHeader } from './MenuHeader';
import { InitiativeAvatar } from './InitiativeAvatar';

export const InitiativeCard = observer(() => {
  const { activeInitiativeSpace, teamsQueryStatus } = useClientStore();
  const [anchorEl, setAnchorEl] = useState(null);

  const hasTeam = !!activeInitiativeSpace.content;

  let teamCardContent = null;
  if (teamsQueryStatus === 'init' || teamsQueryStatus === 'loading') {
    teamCardContent = <MenuCardSkeleton />;
  } else if (!hasTeam) {
    teamCardContent = (
      <div>
        <div className='flex mt-2 items-center ml-3 w-full'>
          <NewTeamIcon />
          <h2 className='ml-4 text-black'>
            {capitalizeFirstLetter('Your Team')}
          </h2>
        </div>
        <div className='flex flex-col items-center m-2'>
          <CreateTeamCard
            iconName='teamWithBackground'
            subTitle={
              <p className='text-center'>
                Seamlessly collaborate with your colleagues.
              </p>
            }
            description={
              <div className='mt-2 text-xs text-gray-500'>{StartFreeTrail}</div>
            }
          />
        </div>
      </div>
    );
  } else {
    const { title: initiativeName = '' } = activeInitiativeSpace.content;

    const handleClick = (event: { target: any }) => {
      setAnchorEl(anchorEl ? null : event.target);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    teamCardContent = (
      <div>
        <div className='flex justify-between w-full items-center'>
          <MenuHeader
            avatar={<InitiativeAvatar />}
            name={initiativeName}
            isProfile={false}
            rightIcon={<ToggleIcon />}
            onItemClick={handleClick}
            className='cursor-pointer hover:bg-gray-100'
          />
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            onClick={handleClose}
            placement='customize-position'
            customizePosition={({ top, height, right, modalStyle }) => ({
              left: `calc(${right - 5}px - ${modalStyle.borderWidth})`,
              top: `calc(${top + height + 5}px - ${modalStyle.borderWidth} + ${
                window.scrollY
              }px)`,
            })}
            id='teamSpacesSwitcher'
            style={{
              overflowY: 'scroll',
              maxHeight: 'calc(50vh)',
            }}
          >
            <WorkspaceSwitcherMenuItems
              excludeProfile
              extraItems={[
                <NewTeamWorkSpace allowHoverIcon key='newInitiative' />,
              ]}
            />
          </Menu>
        </div>

        <MenuList items={getInitiativeMenus()} />
      </div>
    );
  }

  return (
    <Suspense fallback={<MenuCardSkeleton />}>
      <div
        className='rounded mt-2'
        style={{
          border: `1px solid ${themes.color.wfGrey100}`,
        }}
      >
        {teamCardContent}
      </div>
    </Suspense>
  );
});
